import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon, type IconName } from "@ect/ui";
import * as Dialog from "@radix-ui/react-dialog";
import { useQuery } from "@powersync/react";
import { aq } from "@ect/core";
import { navigate } from "../../app/router";

interface Hit { kind: "chantier" | "project" | "person" | "product" | "article"; id: string; label: string; target: string }

/** Ctrl+K global search over the replica (chantiers, projects, persons, products, articles). */
export function GlobalSearch() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const active = q.trim().length >= 2;
  const query = aq.search(active ? q.trim() : "\u0000");
  const { data } = useQuery<Hit>(query.sql, query.params);
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen(true); }
    };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, []);
  const go = (h: Hit) => {
    setOpen(false); setQ("");
    if (h.kind === "chantier") navigate(`chantiers/${h.target}`);
    else if (h.kind === "article") navigate(`chantiers/${h.target}`, { tab: "articles" });
    else if (h.kind === "project") navigate(`projects/${h.target}`);
    else if (h.kind === "person") navigate("personnel");
    else navigate(`products/${h.target}`);
  };
  const icon: Record<Hit["kind"], IconName> = { chantier: "building", project: "briefcase", person: "users", product: "box", article: "document" };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="flex h-10 min-w-0 items-center gap-2 rounded-md border border-line bg-ground px-3 text-xs text-ink-3 hover:border-navy/30"><Icon name="search" size={17} /><span className="hidden xl:inline">{t("design.searchPlaceholder")}</span><span className="xl:hidden">{t("common.search")}</span><kbd className="ms-4 hidden rounded-sm border border-line bg-surface px-1.5 py-0.5 text-[10px] lg:block">Ctrl K</kbd></button>
      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30" />
          <Dialog.Content aria-describedby={undefined} className="fixed start-1/2 top-[15vh] z-50 w-[min(92vw,560px)] -translate-x-1/2 overflow-hidden rounded-lg border border-line bg-surface shadow-soft rtl:translate-x-1/2">
            <Dialog.Title className="sr-only">{t("common.search")}</Dialog.Title>
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} aria-label={t("common.search")} placeholder={t("design.searchHint")} className="h-12 w-full border-b border-line bg-transparent px-4 text-sm outline-none" onKeyDown={(e) => { if (e.key === "Enter" && data[0]) go(data[0]); }} />
            <ul className="max-h-80 overflow-auto p-1">
              {active && data.map((h) => (
                <li key={`${h.kind}-${h.id}`}>
                  <button type="button" onClick={() => go(h)} className="flex w-full items-center gap-3 rounded-sm px-3 py-2 text-start text-sm hover:bg-tint">
                    <span className="w-5 text-ink-3" aria-hidden><Icon name={icon[h.kind]} size={18} /></span>
                    <span className="flex-1 truncate">{h.label}</span>
                    <span className="text-xs text-ink-3">{h.kind}</span>
                  </button>
                </li>
              ))}
              {active && !data.length && <li className="px-3 py-3 text-sm text-ink-3">{t("design.noResults")}</li>}
            </ul>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}
